import {
	Product,
	ProductionOrder,
	ProductSerial,
} from "../erp/manufacturing/Product";
import { Label } from "../bff/LABEL_API";
import {
	BF_NAKED_2SNAPCRADLE,
	BF_NAKED_DOCK,
	BF_NAKED_DOT,
	BF_PACKAGED_2SNAPCRADLE,
	BF_PACKAGED_DOT,
	BF_PACKAGED_4WIRE_CRADLE,
	BF_NAKED_4SNAPCRADLE,
	BF_PACKAGED_4SNAPCRADLE,
	getProductVersion,
	BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD,
	BF_PACKAGED_EEG_ADHESIVE,
	BF_DOUBLE_SIDED_ADHESIVE,
	BF_NAKED_ECG_ADHESIVE,
	BF_NAKED_2SNAPCRADLE_110,
	isCE,
	BF_NAKED_3SNAPCRADLE_LEFT,
	BF_PACKAGED_3SNAPCRADLE_LEFT,
	BF_NAKED_3SNAPCRADLE_RIGHT,
	BF_PACKAGED_3SNAPCRADLE_RIGHT,
	BF_PACKAGED_DOUBLE_SIDED_ADHESIVE,
	getGtinByInternalReference,
	BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX,
	BF_NAKED_ECG_3SNAPCRADLE,
	BF_PACKAGED_ECG_3SNAPCRADLE,
	BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR,
	BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR,
	BF_NAKED_3WIRECRADLE_LEFT,
	BF_PACKAGED_3WIRECRADLE_LEFT,
	BF_NAKED_3WIRECRADLE_RIGHT,
	BF_PACKAGED_3WIRECRADLE_RIGHT,
	BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS,
	BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS,
	BF_NAKED_EEG_ADHESIVE_LEFT,
	BF_NAKED_EEG_ADHESIVE_RIGHT,
	BF_CARDIOCARE_24H_STARTER_KIT,
	BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER,
	BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA,
	BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS,
	BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110,
	BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_110,
	BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110,
	BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_110,
	BF_PACKAGED_EEG_ADHESIVE_114,
	BF_NAKED_EEG_ADHESIVE_LEFT_114,
	BF_NAKED_EEG_ADHESIVE_RIGHT_114,
} from "../Utils";
import { isBlank, isNotBlank } from "../StringUtils";
import { isLot, parseSerial } from "@byteflies/byteflies-serials";
import OdooOperationsService from "../erp/manufacturing/OdooOperationsService";
import {
	MakeLabelRequest,
	MakeLabelRequestDEVICEEnum,
	MakeLabelRequestINFO,
} from "../openapi/kitchenaid/print";

const getOrderLineWithInternalReference = function (
	po: ProductionOrder,
	internalReference: string,
) {
	return po.lines.find((line) => {
		return line.product?.internalReference === internalReference;
	});
};

const isAdhesive = function (product: Product) {
	if (product !== undefined && isNotBlank(product.internalReference)) {
		switch (product.internalReference) {
			case BF_DOUBLE_SIDED_ADHESIVE:
			case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD:
			case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS:
			case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR:
			case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS:
			case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110:
			case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS:
			case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110:
			case BF_PACKAGED_EEG_ADHESIVE:
			case BF_PACKAGED_EEG_ADHESIVE_114:
			case BF_NAKED_EEG_ADHESIVE_LEFT:
			case BF_NAKED_EEG_ADHESIVE_LEFT_114:
			case BF_NAKED_EEG_ADHESIVE_RIGHT:
			case BF_NAKED_EEG_ADHESIVE_RIGHT_114:
			case BF_NAKED_ECG_ADHESIVE:
			case BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR:
			case BF_NAKED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_110:
			case BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA:
			case BF_NAKED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_110:
				return true;
			default:
				break;
		}
	}

	return false;
};

const adhesiveCount = function (productionOrder: ProductionOrder) {
	if (
		productionOrder === undefined ||
		productionOrder.lines === undefined ||
		productionOrder.quantity <= 0
	) {
		return 0;
	}

	const adhesives = productionOrder.lines
		.filter((line) => isAdhesive(line.product))
		.filter((line) => line.product_qty !== undefined && line.product_qty > 0)
		.reduce((sum, line) => sum + line.product_qty, 0);

	return Math.round(adhesives / productionOrder.quantity);
};

export const getDockID = async (
	productionOrder: ProductionOrder,
	svc?: OdooOperationsService,
): Promise<ProductSerial | undefined> => {
	console.log("Trying to find the dock ID");

	// First try to get the dock ID from the PO serial's internal reference
	console.log("Checking if PO's serial contains a device ID that's a dock ID");
	const deviceId = productionOrder?.finishedSerial?.deviceId;
	if (isNotBlank(deviceId) && deviceId.toLowerCase().startsWith("dock-")) {
		return productionOrder?.finishedSerial;
	}

	// Second, look for a docking station in the PO order lines
	console.log("Checking if the PO lines contain a docking station");
	const dockPoLineFromPo = getOrderLineWithInternalReference(
		productionOrder,
		BF_NAKED_DOCK,
	);
	if (
		dockPoLineFromPo &&
		dockPoLineFromPo.serial &&
		isNotBlank(dockPoLineFromPo.serial.deviceId)
	) {
		return dockPoLineFromPo.serial;
	} else {
		console.log("No docking station found in PO lines");
	}
	return undefined;
};

export const getDotID = (
	productionOrder: ProductionOrder,
): string | undefined => {
	const deviceId = productionOrder?.finishedSerial?.deviceId;
	if (isNotBlank(deviceId)) {
		return deviceId;
	}

	const dot = getOrderLineWithInternalReference(productionOrder, BF_NAKED_DOT);
	if (
		dot !== undefined &&
		dot.serial !== undefined &&
		isNotBlank(dot.serial.deviceId)
	) {
		return dot.serial.deviceId;
	}
	return undefined;
};

const getGtin = (productionOrder: ProductionOrder) => {
	const serial = productionOrder!.finishedSerial;
	if (serial === undefined || serial === null) {
		throw new Error("PO serial is not defined");
	}

	let gtin = "";
	if (
		isBlank(gtin) &&
		serial !== undefined &&
		serial.product !== undefined &&
		getGtinByInternalReference(serial.product.internalReference!) !== undefined
	) {
		gtin = getGtinByInternalReference(serial.product.internalReference!)!;
	}

	const udi = serial.udi;
	if (isBlank(gtin) && isNotBlank(udi) && parseSerial(udi) !== undefined) {
		const p = parseSerial(udi);
		if (p !== undefined && p.gtin !== undefined) {
			gtin = p.gtin;
		}
	}

	if (isBlank(gtin)) {
		console.error(
			"GTIN is not defined",
			"udi",
			udi,
			"gtin",
			gtin,
			"serial",
			JSON.stringify(serial),
		);
		throw new Error("GTIN is not defined");
	}
	return gtin;
};

const getUdi = (serial: ProductSerial) => {
	if (serial === undefined || serial === null) {
		throw new Error("PO serial is not defined");
	}
	const udi = serial.udi;
	if (isNotBlank(udi)) {
		return udi;
	}

	const internalReference = serial.product?.internalReference;
	if (isBlank(internalReference)) {
		throw new Error("PO product BF-number is not defined");
	}
	const gtin = getGtinByInternalReference(internalReference!);
	if (isLot(serial.serial)) {
		return `01${gtin}10${serial.serial}`;
	} else {
		return `01${gtin}21${serial.serial}`;
	}
};

const getExpiryDate = (productionOrder: ProductionOrder) => {
	const productionDate = getProductionDate(productionOrder);
	if (productionDate === undefined) {
		return undefined;
	}
	const expiry = new Date(productionDate);
	expiry.setFullYear(expiry.getFullYear() + 1);
	return expiry;
};

export const getProductionDate = (productionOrder: ProductionOrder) => {
	if (
		productionOrder?.date_start !== undefined &&
		typeof productionOrder.date_start === "object"
	) {
		return productionOrder.date_start;
	}
	if (
		productionOrder?.finishedSerial?.creationDate !== undefined &&
		typeof productionOrder?.finishedSerial?.creationDate === "object"
	) {
		return productionOrder?.finishedSerial?.creationDate;
	} else {
		return undefined;
	}
};

export const getBoxID = (serial: ProductSerial) => {
	const s = serial.serial;
	if (isBlank(s)) {
		throw new Error("Serial is blank");
	}
	const boxId = s!.substring(s!.length - 4, s!.length);
	return boxId;
};

export const createLabels = async function (
	productionOrder: ProductionOrder,
	svc: OdooOperationsService,
) {
	const mainLabel = await createLabel(productionOrder, svc);
	return [mainLabel];
};

export const createLabel = async function (
	productionOrder: ProductionOrder,
	svc?: OdooOperationsService,
) {
	console.log(
		`Creating label for production order:\n${JSON.stringify(productionOrder)}`,
	);
	const productionDate = getProductionDate(productionOrder);
	if (productionDate === undefined) {
		throw new Error(`Production date is undefined`);
	}
	const serial = productionOrder?.finishedSerial;
	if (serial === undefined || isBlank(serial.serial)) {
		throw new Error(`PO serial is undefined`);
	}

	const expiryDate = getExpiryDate(productionOrder);
	const udi = getUdi(serial);
	if (udi === undefined) {
		throw new Error(`PO serial udi is undefined`);
	}

	const gtin = getGtin(productionOrder);
	const vn = getProductVersion(productionOrder.product);

	if (
		productionOrder === undefined ||
		productionOrder.product === undefined ||
		isBlank(productionOrder.product.internalReference)
	) {
		throw new Error("Product BF number is blank");
	}

	const info: Label = {
		Date: Date.now(),
		GTIN: gtin,
		SERIAL: serial.serial,
		"BATCH/LOT": isLot(serial.serial) ? serial.serial : undefined,
		"PROD DATE": productionDate,
		"USE BY OR EXPIRY": expiryDate,
		VARIANT: vn,
	};
	let device: MakeLabelRequestDEVICEEnum;

	const bfNumber = productionOrder.product.internalReference!;

	switch (bfNumber) {
		case BF_NAKED_DOT:
		case BF_PACKAGED_DOT:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			const dotId = getDotID(productionOrder);
			if (isBlank(dotId)) {
				throw new Error("Device-ID is blank");
			}
			info["Sensor-Dot"] = {
				UDI: udi,
				ID: dotId!,
				"Version-Number": vn!,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "Sensor-Dot";
			break;
		case BF_NAKED_DOCK:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			const dockId = await getDockID(productionOrder, undefined);
			if (isBlank(dockId?.deviceId)) {
				throw new Error("Device-ID is blank");
			}
			info["Docking-Station"] = {
				UDI: udi,
				ID: dockId?.deviceId!,
				"Version-Number": vn,
			};
			device = "Docking-Station";
			break;
		case BF_NAKED_2SNAPCRADLE:
		case BF_NAKED_2SNAPCRADLE_110:
		case BF_PACKAGED_2SNAPCRADLE:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["2-Snap-Cradle"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "2-Snap-Cradle";
			break;
		case BF_NAKED_ECG_3SNAPCRADLE:
		case BF_PACKAGED_ECG_3SNAPCRADLE:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["ECG-3-Snap-Cradle"] = {
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "ECG-3-Snap-Cradle";
			break;
		case BF_NAKED_3SNAPCRADLE_LEFT:
		case BF_PACKAGED_3SNAPCRADLE_LEFT:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["3-Snap-Cradle-Left"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "3-Snap-Cradle-Left";
			break;
		case BF_NAKED_3SNAPCRADLE_RIGHT:
		case BF_PACKAGED_3SNAPCRADLE_RIGHT:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["3-Snap-Cradle-Right"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "3-Snap-Cradle-Right";
			break;
		case BF_NAKED_3WIRECRADLE_LEFT:
		case BF_PACKAGED_3WIRECRADLE_LEFT:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["3-Wire-Cradle-Left"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "3-Wire-Cradle-Left";
			break;
		case BF_NAKED_3WIRECRADLE_RIGHT:
		case BF_PACKAGED_3WIRECRADLE_RIGHT:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["3-Wire-Cradle-Right"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "3-Wire-Cradle-Right";
			break;
		case BF_NAKED_4SNAPCRADLE:
		case BF_PACKAGED_4SNAPCRADLE:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["4-Snap-Cradle"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "4-Snap-Cradle";
			break;
		case BF_PACKAGED_4WIRE_CRADLE:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Patch"] = {
				UDI: udi,
				"Version-Number": vn,
				CE: isCE(productionOrder.product!.internalReference!, vn),
				"Investigational-Use": !isCE(
					productionOrder.product!.internalReference!,
					vn,
				),
			};
			device = "ExG-Patch";
			break;
		case BF_PACKAGED_DOUBLE_SIDED_ADHESIVE:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Package double-sided Adhesive"] = {
				UDI: udi,
				ProductionDate: productionDate.toISOString(),
				ExpirationDate: expiryDate!.toISOString(),
				LotID: serial.serial!,
				"Version-Number": vn,
				ItemCount: adhesiveCount(productionOrder),
				CE: isCE(productionOrder.product!.internalReference!, vn),
			};
			device = "Package double-sided Adhesive";
			break;
		case BF_CARDIOCARE_MULTI_LEAD_LINEAR_BOX:
			const dockIdCardioMultiLeadLinear = await getDockID(productionOrder, svc);
			if (isBlank(dockIdCardioMultiLeadLinear?.deviceId)) {
				throw new Error("Dock ID is blank");
			} else if (
				dockIdCardioMultiLeadLinear
					?.deviceId!.toLowerCase()
					.indexOf("dock-") === -1
			) {
				throw new Error("Dock ID should be a dock ID");
			}
			const cardioMultiLeadLinearContent = [
				"Byteflies Kit Model 2",
				"Byteflies ECG Adhesives",
				"Byteflies CardioCare@Home Pro",
			];

			info["hw-cardiocare-multi-lead-linear-box"] = {
				ID: dockIdCardioMultiLeadLinear?.deviceId!,
				Title: "CardioCare@Home",
				SubTitle: "Multi Lead Linear",
				ContentTitle: "Procedure Pack Contents",
				Content: cardioMultiLeadLinearContent,
				BoxID: getBoxID(serial),
			};
			device = "CardioCare@Home-Multi-Lead-Linear-box";
			break;
		case BF_CARDIOCARE_24H_STARTER_KIT:
		case BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER:
			const dockIdCardio24hStarterKit = await getDockID(productionOrder, svc);
			if (isBlank(dockIdCardio24hStarterKit?.deviceId)) {
				throw new Error("Dock ID is blank");
			} else if (
				dockIdCardio24hStarterKit?.deviceId!.toLowerCase().indexOf("dock-") ===
				-1
			) {
				throw new Error("Dock ID should be a dock ID");
			}
			const cardio24hStarterKitContent = [
				"Byteflies Kit Model 2",
				"Byteflies ECG Adhesives",
				"Byteflies CardioCare@Home Pro",
			];

			if (bfNumber === BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER) {
				cardio24hStarterKitContent.push("GL.iNet Spitz 4G LTE Router");
			}

			info["hw-cardiocare-24h-starter-kit"] = {
				ID: dockIdCardio24hStarterKit?.deviceId!,
				Title: "CardioCare@Home",
				SubTitle:
					bfNumber === BF_CARDIOCARE_24H_STARTER_KIT_WITH_WIFI_ROUTER
						? "24h Starter Kit with WiFi Router"
						: "24h Starter Kit",
				ContentTitle: "Procedure Pack Contents",
				Content: cardio24hStarterKitContent,
				BoxID: getBoxID(serial),
			};
			device = "CardioCare@Home-24h-Starter-Kit";
			break;
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_2PCS:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Package ECG Adhesive Single Lead"] = {
				UDI: udi,
				ProductionDate: productionDate.getTime() / 1000,
				ExpirationDate: expiryDate!.getTime() / 1000,
				LotID: serial.serial!,
				"Version-Number": vn,
				ItemCount: adhesiveCount(productionOrder),
				CE: isCE(productionOrder.product!.internalReference!, vn),
			};
			device = "Package ECG Adhesive Single Lead";
			break;
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR:
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS:
		case BF_PACKAGED_ECG_ADHESIVE_MULTI_LEAD_LINEAR_2PCS_110:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Package ECG Adhesive Multi Lead Linear"] = {
				UDI: udi,
				ProductionDate: productionDate.getTime() / 1000,
				ExpirationDate: expiryDate!.getTime() / 1000,
				LotID: serial.serial!,
				"Version-Number": vn,
				ItemCount: adhesiveCount(productionOrder),
				CE: isCE(productionOrder.product!.internalReference!, vn),
			};
			device = "Package ECG Adhesive Multi Lead Linear";
			break;
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS:
		case BF_PACKAGED_ECG_ADHESIVE_SINGLE_LEAD_EXTRA_2PCS_110:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Package ECG Adhesive Single Lead Extra"] = {
				UDI: udi,
				ProductionDate: productionDate.getTime() / 1000,
				ExpirationDate: expiryDate!.getTime() / 1000,
				LotID: serial.serial!,
				"Version-Number": vn,
				ItemCount: adhesiveCount(productionOrder),
				CE: isCE(productionOrder.product!.internalReference!, vn),
			};
			device = "Package ECG Adhesive Single Lead Extra";
			break;
		case BF_PACKAGED_EEG_ADHESIVE:
		case BF_PACKAGED_EEG_ADHESIVE_114:
			if (vn === undefined || isBlank(vn)) {
				throw new Error("Version number is blank");
			}
			info["Package EEG Adhesive"] = {
				UDI: udi,
				ProductionDate: productionDate.getTime() / 1000,
				ExpirationDate: expiryDate!.getTime() / 1000,
				LotID: serial.serial!,
				"Version-Number": vn,
				ItemCount: adhesiveCount(productionOrder),
				CE: isCE(productionOrder.product!.internalReference!, vn), //vn.toLowerCase() === "4b" ? false : true,
			};
			device = "Package EEG Adhesive";
			break;
		default:
			throw new Error(`Unknown device type: ${bfNumber}`);
	}

	const request: MakeLabelRequest = {
		DEVICE: device as MakeLabelRequestDEVICEEnum,
		INFO: info as any as MakeLabelRequestINFO,
	};
	return request;
};
